import React, { PropTypes } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';

import BcReactComponent from '@bibliocommons/base-bc-react-component';
import { TextButton } from '@bibliocommons/base-buttons';
import { sendAnalyticsEvents } from '@bibliocommons/utils-analytics';
import { searchEventComposer } from '@bibliocommons/analytics-event-composers';
import EmbeddedChatWidget from '@bibliocommons/shared-embedded-chat-widget';
import { QuestionMarkCircled } from '@bibliocommons/base-icons';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';

import { SAVED_SEARCH_WORKFLOW } from '../../../constants/WorkflowConstants';
import SearchTitle from '../SearchTitle';
import MobileSearchBox from '../MobileSearchBox';
import DidNotFind from '../DidNotFind';
import DidYouMean from '../DidYouMean';
import ExternalSearchLink from '../ExternalSearchLink';
import ChatWidgetButton from '../ChatWidgetButton';

import SearchTitleDeprecated from '../SearchTitleDeprecated';
import FeatureFlagContainer from '../../../containers/FeatureFlagContainer';
import ActiveFilters from '../ActiveFilters';

import './EmptySearchResult.scss';

export class EmptySearchResult extends BcReactComponent {
  constructor(props) {
    super(props);
    this.bindAll('handleDidYouMeanImpression');
  }

  handleDidYouMeanImpression() {
    const { query, didYouMeanQuerySuggestion } = this.props;
    sendAnalyticsEvents(searchEventComposer.composeDidYouMeanImpressionEvent({ query, suggestion: didYouMeanQuerySuggestion }));
  }

  renderActiveFilters() {
    const { activeFilters, locked, singleBranch, searchType } = this.props;

    if (!activeFilters.isEmpty()) {
      return (
        <div className="active-filters">
          <FormattedMessage id="empty_search_clear_filters_info" />
          <ActiveFilters
            searchType={searchType}
            singleBranch={singleBranch}
            activeFilters={activeFilters}
            locked={locked}
            showLockSwitch={false} />
        </div>
      );
    }

    return null;
  }

  renderStaticEmptySearchResults() {
    const { hasStaticEmptySearchResults, intl, query } = this.props;

    if (!hasStaticEmptySearchResults) {
      return null;
    }

    const staticEmptySearchContent = intl.formatMessage({
      id: 'static_empty_search_results_page'
    });

    if (staticEmptySearchContent) {
      const InjectQueryIntoHtml = staticEmptySearchContent.replace(/{query}/gi, query);

      return (
        <div className="static-empty-search-content">
          <div
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: InjectQueryIntoHtml }} />
        </div>
      );
    }

    return null;
  }

  renderSavedSearchLink(updateWorkflow) {
    return (
      <TextButton handleClick={() => updateWorkflow(SAVED_SEARCH_WORKFLOW)} className="save-search-link" data-test-id="save-search-link">
        {this.props.intl.formatMessage({ id: 'save_search' })}
      </TextButton>
    );
  }

  renderSearchBox() {
    const { orderedSearchTypes, locked, query, searchType, activeFilters } = this.props;
    return this.context.mobileApp && (
      <MobileSearchBox
        locked={locked}
        query={query}
        searchType={searchType}
        searchTypes={orderedSearchTypes}
        activeFilters={activeFilters} />
    );
  }

  renderDidNotFind() {
    const { query, didNotFindOptions } = this.props;

    return didNotFindOptions.get('isEnabled') && (
      <div className="empty-search-did-not-find">
        <DidNotFind query={query} didNotFindOptions={didNotFindOptions} />
      </div>
    );
  }

  renderDidYouMean() {
    const { handleDidYouMeanImpression, didYouMeanQuerySuggestion, didYouMeanResultCount } = this.props;
    return (
      <DidYouMean
        key="did-you-mean"
        handleDidYouMeanImpression={handleDidYouMeanImpression}
        querySuggestion={didYouMeanQuerySuggestion}
        resultCount={didYouMeanResultCount} />
    );
  }

  renderExternalSearch() {
    const { query, searchType, showExternalSearchLink } = this.props;

    return showExternalSearchLink && (
      <ExternalSearchLink query={query} searchType={searchType} formatted />
    );
  }

  renderEmbeddedChatWidget() {
    const { chatWidgets } = this.props;
    const supportedWidgets = ['qwidget', 'libanswers', 'libraryh3lp', 'mosio', 'refchatter'];
    const widgets = [];

    if (!chatWidgets) {
      return null;
    }

    supportedWidgets.forEach((widget, index) => {
      if (chatWidgets.get(widget) && chatWidgets.getIn([widget, 'enabled'])) {
        widgets.push(<EmbeddedChatWidget key={index} widget={widget} />);
      }
    });

    if (widgets.length) {
      return (
        <div className="empty-search-embedded-chat-widgets">
          {widgets}
        </div>
      );
    }

    return widgets;
  }

  renderChatWidgetButton() {
    const enabledWidget = this.props.chatWidgets.findLastKey(widget => widget.get('enabled'));

    if (enabledWidget) {
      return (
        <span className="empty-search-chat-widget">
          <ChatWidgetButton widget={enabledWidget} />
        </span>
      );
    }

    return null;
  }

  renderSearchTipsLink() {
    const searchTipsLink = this.props.intl.formatMessage({ id: 'search_tips_link' });

    if (searchTipsLink) {
      return (
        <a
          className="search-tips"
          href={searchTipsLink}
          target="_blank"
          rel="noopener noreferrer">
          <span aria-hidden>
            <QuestionMarkCircled />
            <FormattedMessage id="search_tips_link_text" />
          </span>
          <ScreenReaderMessage>
            <FormattedMessage id="ada_opens_a_new_tab" values={{ linkText: <FormattedMessage id="search_tips_link_text" /> }} />
          </ScreenReaderMessage>
        </a>
      );
    }
    return null;
  }

  renderSearchTitleDeprecated() {
    const { percent, query, searchType, broadened, didYouMeanQuerySuggestion, showExternalSearchLink, drawerActions,
      broadenedSearchType, broadenedSearchCount, didYouMeanResultCount, title, titleKey, suppress, customEdit } = this.props;

    return (
      <SearchTitleDeprecated
        broadened={broadened}
        broadenedSearchCount={broadenedSearchCount}
        broadenedSearchType={broadenedSearchType}
        customEdit={customEdit}
        didYouMeanQuerySuggestion={didYouMeanQuerySuggestion}
        didYouMeanResultCount={didYouMeanResultCount}
        drawerActions={drawerActions}
        id="searchTitle"
        percent={percent}
        query={query}
        searchType={searchType}
        showExternalSearchLink={showExternalSearchLink}
        suppress={suppress}
        title={title}
        titleKey={titleKey} />
    );
  }

  renderMainContents() {
    const { query } = this.props;
    return (
      <div className="empty-search-main-content">
        <div className="empty-search-sub-title">
          {query &&
            <FormattedMessage
              id="Search.empty_search"
              tagName="h4"
              values={{ query: <strong>{query}</strong> }} />
          }
          {this.renderDidYouMean()}
          {this.renderActiveFilters()}
          {this.renderExternalSearch()}
        </div>
        {this.renderSearchTools()}
      </div>
    );
  }

  renderSecondaryContents() {
    return !this.props.didNotFindOptions.get('isEnabled') && (
      <div className="empty-search-secondary-content">
        {this.renderStaticEmptySearchResults()}
        {this.renderEmbeddedChatWidget()}
      </div>
    );
  }

  renderSearchTools() {
    return this.props.didNotFindOptions.get('isEnabled') && (
      <div className="empty-search-help-tools">
        {this.renderSearchTipsLink()}
        {this.renderChatWidgetButton()}
      </div>
    );
  }

  render() {
    const { query, title, titleKey, didYouMeanQuerySuggestion, didYouMeanResultCount, searchType, broadened, broadenedSearchType,
      showExternalSearchLink, rssURL, customEdit, suppress, didNotFindOptions } = this.props;

    return (
      <div className="cp-empty-search-result">
        {this.renderSearchBox()}
        <FeatureFlagContainer>
          {
            flags => flags.get('searchImprovements') ? (
              <SearchTitle
                broadened={broadened}
                broadenedSearchType={broadenedSearchType}
                customEdit={customEdit}
                didYouMeanQuerySuggestion={didYouMeanQuerySuggestion}
                didYouMeanResultCount={didYouMeanResultCount}
                showDidYouMean={false}
                id="searchTitle"
                query={query}
                searchType={searchType}
                showExternalSearchLink={showExternalSearchLink}
                suppress={suppress}
                title={title}
                titleKey={titleKey}
                handleDidYouMeanImpression={this.handleDidYouMeanImpression}
                rssURL={rssURL}
                didNotFindOptions={didNotFindOptions} />
            ) : this.renderSearchTitleDeprecated()
          }
        </ FeatureFlagContainer>
        {this.renderMainContents()}
        {this.renderSecondaryContents()}
        {this.renderDidNotFind()}
      </div>
    );
  }
}

EmptySearchResult.displayName = 'EmptySearchResult';
EmptySearchResult.propTypes = {
  query: PropTypes.string,
  title: PropTypes.string,
  titleKey: PropTypes.string,
  activeFilters: ImmutablePropTypes.mapOf(PropTypes.object).isRequired,
  chatWidgets: ImmutablePropTypes.mapOf(PropTypes.object),
  orderedSearchTypes: ImmutablePropTypes.list,
  didYouMeanQuerySuggestion: PropTypes.string,
  didYouMeanResultCount: PropTypes.number,
  intl: intlShape,
  search: PropTypes.func.isRequired,
  hasStaticEmptySearchResults: PropTypes.bool.isRequired,
  searchType: PropTypes.string,
  broadened: PropTypes.bool,
  broadenedSearchType: PropTypes.string,
  showExternalSearchLink: PropTypes.bool,
  locked: PropTypes.bool,
  rssURL: PropTypes.string,
  customEdit: PropTypes.bool,
  suppress: PropTypes.bool,
  didNotFindOptions: ImmutablePropTypes.map,
  singleBranch: PropTypes.bool
};

EmptySearchResult.contextTypes = {
  mobileApp: PropTypes.bool
};

export default injectIntl(EmptySearchResult);



// WEBPACK FOOTER //
// ./app/components/search/EmptySearchResult/EmptySearchResult.jsx